import React, { useMemo, useState, useCallback } from 'react';
import { RSVP, DietaryPreference } from '../types.ts';
import { EVENT_TITLE } from '../constants.ts';

interface DashboardProps {
  rsvps: RSVP[];
  onGoToRegistration: () => void; // New prop for navigating back
}

export const Dashboard: React.FC<DashboardProps> = ({ rsvps, onGoToRegistration }) => {
  const [attendanceFilter, setAttendanceFilter] = useState<'all' | 'attending' | 'not_attending'>('all');
  const [dietaryFilter, setDietaryFilter] = useState<DietaryPreference | 'all'>('all');
  const [sortOrder, setSortOrder] = useState<'none' | 'name_asc' | 'name_desc'>('none'); // New state for sorting

  const summary = useMemo(() => {
    const totalAttending = rsvps.filter(r => r.attending).length;
    const totalPeople = totalAttending;

    const dietaryCounts = rsvps.reduce((counts, rsvp) => {
      if (rsvp.attending) {
        counts[rsvp.dietaryPreference] = (counts[rsvp.dietaryPreference] || 0) + 1;
      }
      return counts;
    }, {} as Record<DietaryPreference, number>);

    return {
      totalRSVPs: rsvps.length,
      totalAttending,
      totalPeople,
      dietaryCounts,
    };
  }, [rsvps]);

  const filteredAndSortedRsvps = useMemo(() => {
    let filtered = [...rsvps]; // Create a mutable copy

    // Apply attendance filter
    if (attendanceFilter === 'attending') {
      filtered = filtered.filter(rsvp => rsvp.attending);
    } else if (attendanceFilter === 'not_attending') {
      filtered = filtered.filter(rsvp => !rsvp.attending);
    }

    // Apply dietary preference filter
    if (dietaryFilter !== 'all') {
      filtered = filtered.filter(rsvp => rsvp.dietaryPreference === dietaryFilter);
    }

    // Apply sorting
    if (sortOrder === 'name_asc') {
      filtered.sort((a, b) => a.name.localeCompare(b.name));
    } else if (sortOrder === 'name_desc') {
      filtered.sort((a, b) => b.name.localeCompare(a.name));
    } else {
      // Default sort by most recent first if no other sort is applied
      filtered.sort((a, b) => b.timestamp - a.timestamp); 
    }

    return filtered;
  }, [rsvps, attendanceFilter, dietaryFilter, sortOrder]);

  const exportToCsv = useCallback(() => {
    if (filteredAndSortedRsvps.length === 0) {
      alert("No data to export.");
      return;
    }

    const headers = ["Name", "Attending", "Dietary Preference", "Timestamp"];
    const csvRows = [
      headers.join(','), // CSV header row
      ...filteredAndSortedRsvps.map(rsvp => {
        const attendingStatus = rsvp.attending ? 'Yes' : 'No';
        const timestamp = new Date(rsvp.timestamp).toLocaleString();
        // Basic CSV escaping for values
        const escapeCsv = (value: string | number) => `"${String(value).replace(/"/g, '""')}"`;

        return [
          escapeCsv(rsvp.name),
          escapeCsv(attendingStatus),
          escapeCsv(rsvp.dietaryPreference),
          escapeCsv(timestamp),
        ].join(',');
      })
    ];

    const csvString = csvRows.join('\n');
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) { // Feature detection for download attribute
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', 'rsvps_export.csv');
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } else {
      alert("Your browser does not support downloading files directly.");
    }
  }, [filteredAndSortedRsvps]);

  return (
    <div className="p-4 md:p-8 bg-primary-bg rounded-lg font-montserrat w-full">
      <div className="flex justify-between items-center mb-8">
        <h2 className="text-3xl font-playfair font-bold text-accent-gold text-center flex-grow">"{EVENT_TITLE}" Dashboard</h2>
        <button
          onClick={onGoToRegistration}
          className="ml-4 px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-primary-bg bg-accent-gold hover:bg-opacity-80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-gold transition duration-300 ease-in-out"
          aria-label="Go back to registration page"
        >
          Go back to Registration
        </button>
      </div>

      {/* Summary Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        <div className="bg-primary-bg border border-accent-gold p-4 rounded-lg shadow-sm text-center">
          <p className="text-sm font-medium text-accent-gold">Total RSVPs</p>
          <p className="text-3xl font-bold text-text-white">{summary.totalRSVPs}</p>
        </div>
        <div className="bg-primary-bg border border-accent-gold p-4 rounded-lg shadow-sm text-center">
          <p className="text-sm font-medium text-accent-gold">Attending</p>
          <p className="text-3xl font-bold text-text-white">{summary.totalAttending}</p>
        </div>
        <div className="bg-primary-bg border border-accent-gold p-4 rounded-lg shadow-sm text-center">
          <p className="text-sm font-medium text-accent-gold">Confirmed Attendees</p>
          <p className="text-3xl font-bold text-text-white">{summary.totalPeople}</p>
        </div>
      </div>

      {/* Dietary Breakdown */}
      <div className="mb-8">
        <h3 className="text-2xl font-playfair font-medium text-accent-gold mb-4">Dietary Breakdown</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {Object.values(DietaryPreference)
            .filter(pref => pref !== DietaryPreference.N_A) // Exclude 'Not Applicable' from display
            .map(pref => (
              <div key={pref} className="bg-primary-bg border border-accent-gold p-3 rounded-lg flex justify-between items-center shadow-sm">
                <span className="text-text-white">{pref}:</span>
                <span className="font-bold text-accent-gold">{summary.dietaryCounts[pref] || 0}</span>
              </div>
            ))}
        </div>
      </div>

      {/* Filter Options */}
      <div className="mb-8 p-4 bg-primary-bg border border-accent-gold rounded-lg shadow-sm flex flex-col sm:flex-row justify-between items-center gap-4">
        <div className="w-full sm:w-1/2">
          <label htmlFor="attendanceFilter" className="block text-sm font-medium text-text-white mb-1">Filter by Attendance</label>
          <select
            id="attendanceFilter"
            value={attendanceFilter}
            onChange={(e) => setAttendanceFilter(e.target.value as 'all' | 'attending' | 'not_attending')}
            className="mt-1 block w-full px-4 py-2 border border-accent-gold rounded-md shadow-sm focus:ring-accent-gold focus:border-accent-gold sm:text-sm bg-primary-bg text-text-white"
            aria-label="Filter RSVPs by attendance status"
          >
            <option value="all">All</option>
            <option value="attending">Attending</option>
            <option value="not_attending">Not Attending</option>
          </select>
        </div>
        <div className="w-full sm:w-1/2">
          <label htmlFor="dietaryFilter" className="block text-sm font-medium text-text-white mb-1">Filter by Dietary Preference</label>
          <select
            id="dietaryFilter"
            value={dietaryFilter}
            onChange={(e) => setDietaryFilter(e.target.value as DietaryPreference | 'all')}
            className="mt-1 block w-full px-4 py-2 border border-accent-gold rounded-md shadow-sm focus:ring-accent-gold focus:border-accent-gold sm:text-sm bg-primary-bg text-text-white"
            aria-label="Filter RSVPs by dietary preference"
          >
            <option value="all">All</option>
            {Object.values(DietaryPreference)
              .filter(pref => pref !== DietaryPreference.N_A)
              .map(pref => (
                <option key={pref} value={pref}>{pref}</option>
              ))}
            <option value={DietaryPreference.N_A}>Not Applicable</option> {/* Show N/A for non-attendees */}
          </select>
        </div>
      </div>

      {/* Individual RSVPs List */}
      <h3 className="text-2xl font-playfair font-medium text-accent-gold mb-4">Individual RSVPs</h3>
      
      {/* Sort and Export Options */}
      <div className="mb-6 p-4 bg-primary-bg border border-accent-gold rounded-lg shadow-sm flex flex-col sm:flex-row justify-between items-center gap-4">
        <div className="w-full sm:w-1/2">
          <label htmlFor="sortOrder" className="block text-sm font-medium text-text-white mb-1">Sort by Name</label>
          <select
            id="sortOrder"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value as 'none' | 'name_asc' | 'name_desc')}
            className="mt-1 block w-full px-4 py-2 border border-accent-gold rounded-md shadow-sm focus:ring-accent-gold focus:border-accent-gold sm:text-sm bg-primary-bg text-text-white"
            aria-label="Sort RSVPs by name"
          >
            <option value="none">Default (Most Recent)</option>
            <option value="name_asc">Name (A-Z)</option>
            <option value="name_desc">Name (Z-A)</option>
          </select>
        </div>
        <div className="w-full sm:w-1/2 flex justify-end">
          <button
            onClick={exportToCsv}
            className="w-full sm:w-auto mt-4 sm:mt-0 inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-primary-bg bg-soft-gold hover:bg-opacity-80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-soft-gold transition duration-300 ease-in-out"
            aria-label="Export RSVPs to CSV"
          >
            Export to CSV
          </button>
        </div>
      </div>

      {filteredAndSortedRsvps.length === 0 ? (
        <p className="text-text-white text-center">No RSVPs found matching your criteria.</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-accent-gold">
          <table className="min-w-full divide-y divide-accent-gold">
            <thead className="bg-primary-bg">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-accent-gold uppercase tracking-wider">
                  Name
                </th>
                {/* Email column removed as it's not present in the RSVP type */}
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-accent-gold uppercase tracking-wider">
                  Attending
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-accent-gold uppercase tracking-wider">
                  Dietary
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-accent-gold uppercase tracking-wider">
                  Time
                </th>
              </tr>
            </thead>
            <tbody className="bg-primary-bg divide-y divide-accent-gold">
              {filteredAndSortedRsvps.map((rsvp) => (
                <tr key={rsvp.id} className="hover:bg-dark-blue-light transition-colors duration-200">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-text-white">
                    {rsvp.name}
                  </td>
                  {/* Email row data removed */}
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${rsvp.attending ? 'bg-green-700 text-white' : 'bg-red-700 text-white'}`}>
                      {rsvp.attending ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-text-white">
                    {rsvp.dietaryPreference}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-text-white">
                    {new Date(rsvp.timestamp).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};